// src/pages/supplier/SubscriptionPage.jsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supplierSubscriptionApi } from '../../api/services'
import { Card, CardBody, StatusBadge, Button, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function SubscriptionPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['subscription'], queryFn: () => supplierSubscriptionApi.index() })
  const startTrial = useMutation({ mutationFn: () => supplierSubscriptionApi.startTrial(), onSuccess: () => { toast.success('Trial started!'); qc.invalidateQueries(['subscription']) }, onError: e => toast.error(e.response?.data?.message || 'Error') })

  if (isLoading) return <PageSpinner />
  const { plans = [], current, history = [] } = data?.data?.data || {}

  return (
    <div>
      <h1 className="text-xl font-display font-bold mb-6">Subscription Plans</h1>

      {current && (
        <Card className="mb-6 border-pink">
          <CardBody>
            <p className="text-sm font-bold mb-1">Current Plan: <span className="text-pink">{current.plan?.name}</span></p>
            <div className="flex items-center gap-3">
              <StatusBadge status={current.status} />
              {current.expires_at && <p className="text-xs text-muted">Expires: {new Date(current.expires_at).toLocaleDateString('en-IN')}</p>}
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {plans.map(plan => (
          <Card key={plan.id} className={current?.plan_id === plan.id ? 'border-pink' : ''}>
            <CardBody>
              <h3 className="font-bold text-gray-900">{plan.name}</h3>
              <p className="text-2xl font-display font-bold text-pink mt-1">
                ₹{Number(plan.monthly_price).toLocaleString('en-IN')}
                <span className="text-sm text-muted font-normal">/mo</span>
              </p>
              {plan.annual_price && (
                <p className="text-xs text-green-600 mt-0.5">₹{Number(plan.annual_price).toLocaleString('en-IN')}/yr (save ~17%)</p>
              )}
              <ul className="mt-3 space-y-1 text-xs text-gray-600">
                {plan.product_limit && <li>✓ Up to {plan.product_limit} products</li>}
                {plan.analytics_enabled && <li>✓ Analytics dashboard</li>}
                {plan.storefront_enabled && <li>✓ Custom storefront</li>}
                {plan.rfq_enabled && <li>✓ RFQ access</li>}
                {plan.featured_listing && <li>✓ Featured listing</li>}
              </ul>
              <div className="mt-4">
                {current?.plan_id === plan.id ? (
                  <Button size="sm" variant="secondary" disabled>Current Plan</Button>
                ) : current ? (
                  <Button size="sm">Upgrade</Button>
                ) : (
                  <Button size="sm" loading={startTrial.isPending} onClick={() => startTrial.mutate()}>Start Free Trial</Button>
                )}
              </div>
            </CardBody>
          </Card>
        ))}
        {plans.length === 0 && <p className="text-sm text-muted col-span-3 py-8 text-center">No plans available.</p>}
      </div>

      {history.length > 0 && (
        <Card>
          <CardBody>
            <p className="font-bold text-sm mb-3">Subscription History</p>
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-b-0">
                  <span>{h.plan?.name}</span>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={h.status} />
                    <span className="text-muted text-xs">{new Date(h.created_at).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
